import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Search, Plus, Upload, Users, RefreshCw, GitMerge, Pencil, Loader2, AtSign } from 'lucide-react';
import { PANEL, CATEGORY_META, CATEGORY_ORDER } from './mailUtils';
import { ContactEditor, AB_API, abAuth } from './ContactEditor';
import { ContactDuplicates } from './ContactDuplicates';

const BTN = 'h-9 px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50';

/** Mail → Contacts: the 365 Global Address Book. */
export default function ContactsPage() {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [sort, setSort] = useState('name');
  const [data, setData] = useState({ rows: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dupes, setDupes] = useState(false);
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);

  const loadStats = useCallback(() => axios.get(`${AB_API}/stats`, abAuth()).then(r => setStats(r.data)).catch(() => {}), []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const domain = q.trim().startsWith('@') ? q.trim().slice(1) : '';
      const r = await axios.get(AB_API, { ...abAuth(), params: { q: domain ? '' : q.trim(), domain, category: cat, sort, limit: 300 } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load contacts'); }
    setLoading(false);
  }, [q, cat, sort]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const quickCat = async (c, category) => {
    try { const r = await axios.put(`${AB_API}/${c.id}`, { category }, abAuth()); setData(d => ({ ...d, rows: d.rows.map(x => (x.id === c.id ? r.data : x)) })); loadStats(); }
    catch (e) { toast.error('Could not update'); }
  };
  const importCsv = async (file) => {
    setBusy('import');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${AB_API}/import`, fd, abAuth());
      toast.success(`Imported: ${r.data.added} new, ${r.data.updated} updated, ${r.data.skipped} skipped`); load(); loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    setBusy('');
  };
  const harvest = async () => {
    setBusy('harvest');
    try { const r = await axios.post(`${AB_API}/harvest-now`, {}, { ...abAuth(), timeout: 180000 }); toast.success(r.data.found ? `Checked your Sent mail — ${r.data.found} new addresses added` : `Checked ${r.data.checked} recent sent messages — nothing new since the last scan`); load(); loadStats(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not scan sent mail'); }
    setBusy('');
  };

  return (
    <div className="-m-3 lg:-m-6 p-3 lg:p-6 min-h-full ops-canvas space-y-3" data-testid="contacts-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/mail" className="p-2 rounded-lg hover:bg-white text-slate-600" title="Back to Mail" data-testid="contacts-back"><ArrowLeft className="w-4 h-4" /></Link>
        <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2"><Users className="w-6 h-6 text-sky-700" />Contacts</h1>
        {stats && <span className="text-xs text-slate-600" data-testid="contacts-total">{stats.total.toLocaleString()} in the 365 address book · suggested in every To / Cc field</span>}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button onClick={harvest} disabled={!!busy} className={BTN} title="Scan my Sent mail for addresses" data-testid="contacts-harvest">{busy === 'harvest' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}Scan my sent mail</button>
          <button onClick={() => setDupes(true)} className={BTN} data-testid="contacts-dupes"><GitMerge className="w-4 h-4" />Duplicates</button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => { if (e.target.files?.[0]) importCsv(e.target.files[0]); e.target.value = ''; }} data-testid="contacts-import-input" />
          <button onClick={() => fileRef.current?.click()} disabled={!!busy} className={BTN} data-testid="contacts-import">{busy === 'import' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Import CSV</button>
          <button onClick={() => setEditing({})} className="h-9 px-4 rounded-xl bg-sky-600 hover:bg-sky-500 text-white text-sm font-bold inline-flex items-center gap-1.5" data-testid="contacts-add"><Plus className="w-4 h-4" />Add contact</button>
        </div>
      </div>

      {stats && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="contacts-category-chips">
          <button onClick={() => setCat('')} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${!cat ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white/80 border-slate-200 text-slate-700'}`} data-testid="contacts-cat-all">All {stats.total}</button>
          {stats.by_category.map(c => (
            <button key={c.category} onClick={() => setCat(cat === c.category ? '' : c.category)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${cat === c.category ? 'ring-2 ring-sky-300 ' : ''}${CATEGORY_META[c.category].cls}`} data-testid={`contacts-cat-${c.category}`}>{c.label} {c.n}</button>
          ))}
          <span className="text-[11px] text-slate-500 ml-2 hidden md:inline">Top domains:</span>
          {stats.top_domains.slice(0, 6).map(d => <button key={d.domain} onClick={() => setQ(`@${d.domain}`)} className="hidden md:inline-flex items-center gap-1 text-[11px] text-sky-800 hover:underline" data-testid={`contacts-domain-${d.domain}`}><AtSign className="w-3 h-3" />{d.domain} <span className="text-slate-400">{d.n}</span></button>)}
        </div>
      )}

      <div className={`${PANEL} p-3 flex items-center gap-2 flex-wrap`}>
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, company, email — or @domain.com for everyone at a company" className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-sky-500" data-testid="contacts-search" />
        </div>
        <select value={sort} onChange={e => setSort(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 px-2" data-testid="contacts-sort">
          <option value="name">Name A–Z</option><option value="company">Company</option><option value="recent">Recently emailed</option><option value="popular">Most emailed</option>
        </select>
        <span className="text-xs text-slate-600" data-testid="contacts-count"><b className="text-slate-900">{data.total}</b> shown{data.total > data.rows.length ? ` (first ${data.rows.length})` : ''}</span>
      </div>

      <div className={`${PANEL} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/90 border-b border-slate-200">
              <th className="text-left px-3 py-2 font-bold">Name</th><th className="text-left px-3 py-2 font-bold">Email</th><th className="text-left px-3 py-2 font-bold hidden md:table-cell">Company</th>
              <th className="text-left px-3 py-2 font-bold">Category</th><th className="text-left px-3 py-2 font-bold hidden lg:table-cell">Title</th><th className="text-right px-3 py-2 font-bold hidden lg:table-cell">Emailed</th><th className="px-2 py-2" />
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map(c => (
                <tr key={c.id} className="hover:bg-sky-50/60" data-testid={`contact-row-${c.id}`}>
                  <td className="px-3 py-2 text-slate-900 font-medium">{c.name || <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-slate-700 font-mono text-xs">{c.email}</td>
                  <td className="px-3 py-2 text-slate-700 hidden md:table-cell">{c.company}</td>
                  <td className="px-3 py-2">
                    <select value={c.category} onChange={e => quickCat(c, e.target.value)} className={`text-[11px] font-semibold rounded-md border px-1.5 py-0.5 ${CATEGORY_META[c.category]?.cls}`} data-testid={`contact-cat-${c.id}`}>
                      {CATEGORY_ORDER.map(k => <option key={k} value={k}>{CATEGORY_META[k].label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-slate-600 hidden lg:table-cell">{c.title}</td>
                  <td className="px-3 py-2 text-right text-slate-500 tabular-nums hidden lg:table-cell">{c.seen_count || ''}</td>
                  <td className="px-2 py-2 text-right"><button onClick={() => setEditing(c)} className="p-1.5 rounded-lg text-slate-500 hover:bg-white hover:text-slate-900" title="Edit" data-testid={`contact-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
              {!loading && !data.rows.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="contacts-empty">No contacts match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <ContactEditor contact={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { load(); loadStats(); }} onDeleted={() => { load(); loadStats(); }} />}
      {dupes && <ContactDuplicates onClose={() => setDupes(false)} onMerged={() => { load(); loadStats(); }} />}
    </div>
  );
}
